import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Switch,
  Autocomplete,
  Chip,
  Alert,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import productService from '../../../services/productService';
import { useCurrency } from '../../../hooks/useCurrency';

const ProductVariantForm = () => {
  const navigate = useNavigate();
  const { productId, id } = useParams();
  const isEdit = Boolean(id);
  const { t } = useTranslation();

  const { currencySymbol } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState(null);
  const [availableAttributes, setAvailableAttributes] = useState([]);
  const [attributeValueOptions, setAttributeValueOptions] = useState({});

  const [formData, setFormData] = useState({
    productId: productId,
    name: '',
    sku: '',
    price: '',
    stockQuantity: '',
    reservedQuantity: '0',
    isActive: true,
    weight: '',
    variantImageUrls: [],
    attributeValueIds: [],
  });

  const [selectedAttributeValues, setSelectedAttributeValues] = useState([]);

  useEffect(() => {
    fetchProduct();
    fetchAttributes();
    if (isEdit) {
      fetchVariant();
    }
  }, [productId, id]);

  const fetchProduct = async () => {
    try {
      const data = await productService.getProductById(productId);
      setProduct(data);
    } catch (error) {
      toast.error(t('products.fetchError'));
      console.error(error);
    }
  };

  const fetchAttributes = async () => {
    try {
      // Inactive attributes aren't offered in variant creation
      const data = (await productService.getAllAttributes()).filter((a) => a.isActive !== false);
      // Sort attributes by sortOrder
      const sortedAttributes = data.sort((a, b) => (a.sortOrder || 999) - (b.sortOrder || 999));
      setAvailableAttributes(sortedAttributes);

      // Fetch values for each attribute
      const valueOptionsMap = {};
      for (const attr of sortedAttributes) {
        const values = await productService.getAttributeValues(attr.id);
        valueOptionsMap[attr.id] = values;
      }
      setAttributeValueOptions(valueOptionsMap);
    } catch (error) {
      toast.error(t('attributes.fetchError'));
      console.error(error);
    }
  };

  const fetchVariant = async () => {
    try {
      const data = await productService.getProductVariantById(id);
      setFormData({
        productId: data.productId,
        // shortName = what is stored (attribute values only); `name` is the
        // full display name and must not be sent back as the stored name.
        name: data.shortName ?? data.name,
        sku: data.sku,
        price: data.price,
        stockQuantity: data.stockQuantity,
        reservedQuantity: data.reservedQuantity,
        isActive: data.isActive,
        weight: data.weight || '',
        variantImageUrls: data.variantImageUrls || [],
        attributeValueIds: data.attributes.map(attr => attr.valueId),
      });

      // Set selected attribute values
      setSelectedAttributeValues(data.attributes.map(attr => ({
        id: attr.valueId,
        attributeId: attr.attributeId,
        attributeName: attr.attributeName,
        displayValue: attr.displayValue,
      })));
    } catch (error) {
      toast.error(t('common.loadError'));
      console.error(error);
    }
  };

  const handleAttributeValueSelect = (attributeId, value) => {
    if (!value) {
      // Remove the attribute if value is cleared
      setSelectedAttributeValues(selectedAttributeValues.filter(av => av.attributeId !== attributeId));
      return;
    }

    // Remove any existing value for this attribute
    const filtered = selectedAttributeValues.filter(
      av => av.attributeId !== attributeId
    );

    // Add new value
    const newSelectedValues = [...filtered, {
      id: value.id,
      attributeId: attributeId,
      attributeName: availableAttributes.find(a => a.id === attributeId)?.name,
      displayValue: value.displayValue,
    }];

    // Sort by attribute sortOrder for consistent preview
    newSelectedValues.sort((a, b) => {
      const attrA = availableAttributes.find(attr => attr.id === a.attributeId);
      const attrB = availableAttributes.find(attr => attr.id === b.attributeId);
      return (attrA?.sortOrder || 999) - (attrB?.sortOrder || 999);
    });

    setSelectedAttributeValues(newSelectedValues);
  };

  // Generate preview name
  const generatePreviewName = () => {
    if (!product || selectedAttributeValues.length === 0) return '';
    // Only the attribute part is stored; the product name is added at display time.
    return selectedAttributeValues.map(av => av.displayValue).join(' - ');
  };

  // Generate preview SKU
  const generatePreviewSku = () => {
    if (!product || selectedAttributeValues.length === 0) return '';
    return `${product.sku}-${selectedAttributeValues.map(av => av.displayValue.toUpperCase()).join('-')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedAttributeValues.length === 0) {
      toast.error(t('attributes.values') + ' ' + t('common.add'));
      return;
    }

    setLoading(true);
    try {
      const variantData = {
        productId: formData.productId,
        // ✅ FIXED: Send undefined if empty to trigger auto-generation
        name: formData.name.trim() === '' ? undefined : formData.name.trim(),
        sku: formData.sku.trim() === '' ? undefined : formData.sku.trim(),
        price: parseFloat(formData.price),
        stockQuantity: parseInt(formData.stockQuantity),
        reservedQuantity: parseInt(formData.reservedQuantity) || 0,
        isActive: formData.isActive,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        variantImageUrls: formData.variantImageUrls,
        attributeValueIds: selectedAttributeValues.map(av => av.id),
      };

      if (isEdit) {
        await productService.updateProductVariant(id, variantData);
        toast.success(t('variants.updateSuccess'));
      } else {
        await productService.createProductVariant(variantData);
        toast.success(t('variants.createSuccess'));
      }
      navigate(`/products/view/${productId}`);
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.saveError'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Prevent manual variant creation - only editing is allowed
  if (!isEdit) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ mb: 3 }}>
          {t('variants.createVariant')}
        </Typography>

        {product && (
          <Typography variant="subtitle1" sx={{ mb: 3 }} color="text.secondary">
            {t('variants_ext.productLabel', { name: product.name, sku: product.sku })}
          </Typography>
        )}

        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('variants_ext.manualCreationDisabledTitle')}
          </Typography>
          <Typography variant="body1">
            {t('variants_ext.manualCreationDisabledBody')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            {t('variants_ext.manualCreationSteps')}
          </Typography>
          <ol style={{ marginTop: '8px', paddingLeft: '20px' }}>
            <li>{t('variants_ext.stepGoToPO')}</li>
            <li>{t('variants_ext.stepCreatePO')}</li>
            <li>{t('variants_ext.stepAddProduct')}</li>
            <li>{t('variants_ext.stepSpecifyAttrs')}</li>
            <li>{t('variants_ext.stepAutoCreated')}</li>
          </ol>
        </Alert>

        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            onClick={() => navigate(`/products/view/${productId}`)}
            sx={{ mr: 2 }}
          >
            {t('common.back')}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/purchase-orders/create')}
          >
            {t('purchaseOrders.createPO')}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {t('variants.editVariant')}
      </Typography>

      {product && (
        <Typography variant="subtitle1" sx={{ mb: 3 }} color="text.secondary">
          Product: {product.name} ({product.sku})
        </Typography>
      )}

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Show preview if name is empty and attributes are selected */}
              {formData.name.trim() === '' && selectedAttributeValues.length > 0 && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    <strong>{t('variants_ext.namePreview')}</strong> {generatePreviewName()}
                    <br />
                    <strong>{t('variants_ext.skuPreview')}</strong> {generatePreviewSku()}
                  </Alert>
                </Grid>
              )}

              {/* Variant Name - NOW OPTIONAL */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('variants_ext.variantNameOptional')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  helperText={t('variants_ext.nameHelper')}
                  placeholder={generatePreviewName() || t('variants_ext.namePlaceholderExample')}
                />
              </Grid>

              {/* SKU - OPTIONAL */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('variants_ext.skuOptional')}
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  helperText={t('variants_ext.skuHelper')}
                  placeholder={generatePreviewSku() || t('variants_ext.skuPlaceholderExample')}
                />
              </Grid>

              {/* Price */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={`${t('variants_ext.priceDzd')} (${currencySymbol})`}
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>

              {/* Stock Quantity */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('variants.stockQuantity')}
                  type="number"
                  value={formData.stockQuantity}
                  onChange={(e) => setFormData({ ...formData, stockQuantity: e.target.value })}
                  required
                  inputProps={{ min: 0 }}
                />
              </Grid>

              {/* Weight */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('variants.weight')}
                  type="number"
                  value={formData.weight}
                  onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>

              {/* Attributes */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {t('variants_ext.variantAttributes')}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('variants_ext.attributesHelp')}
                </Typography>
              </Grid>

              {availableAttributes.map((attribute) => (
                <Grid item xs={12} md={6} key={attribute.id}>
                  <Autocomplete
                    options={attributeValueOptions[attribute.id] || []}
                    getOptionLabel={(option) => option.displayValue}
                    value={selectedAttributeValues.find(av => av.attributeId === attribute.id) || null}
                    onChange={(event, newValue) => handleAttributeValueSelect(attribute.id, newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={attribute.name}
                        required={attribute.isRequired}
                      />
                    )}
                  />
                </Grid>
              ))}

              {/* Selected Attributes Display */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedAttributeValues.map((av) => (
                    <Chip
                      key={av.id}
                      label={`${av.attributeName}: ${av.displayValue}`}
                      color="primary"
                      onDelete={() => {
                        setSelectedAttributeValues(
                          selectedAttributeValues.filter(v => v.id !== av.id)
                        );
                      }}
                    />
                  ))}
                </Box>
              </Grid>

              {/* Active Status */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label={t('common.active')}
                />
              </Grid>

              {/* Form Actions */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button onClick={() => navigate(`/products/view/${productId}`)} disabled={loading}>
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" variant="contained" disabled={loading}>
                    {loading ? t('common.save') + '...' : isEdit ? t('variants.editVariant') : t('variants.createVariant')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProductVariantForm;